import { useMemo } from 'react'
import {
  MilestoneDetail,
  MilestoneScope,
} from '../../../../domain/entity/MilestoneEntity'
import { ProjectPlanNewRow, NewWbsItemRow } from '../projectPlanNew'

const EMPTY_SCOPE: MilestoneScope = {
  rootMilestones: [],
  descendantMilestonesByWbsItemUuid: new Map(),
}

export const classifyMilestones = (
  data: ProjectPlanNewRow[],
  milestones: MilestoneDetail[],
  rootWbsItem: NewWbsItemRow | undefined,
  isWbsItemTargetEnabled: boolean
): MilestoneScope => {
  if (!milestones.length) return EMPTY_SCOPE

  // 対象を設定できない場合、プロジェクト全体のマイルストーンだけをガントヘッダーに
  // 表示する(対象機能の導入前と同じ表示)。
  // 対象付きのマイルストーンは、機能が有効だった間に作られたものが残りうる。配下という
  // 概念を持たないテナントでこれをヘッダーに並べると、ドリルダウンしても全件が縦線として
  // 出続けてしまうため除外する。
  if (!isWbsItemTargetEnabled) {
    return {
      rootMilestones: milestones.filter(milestone => !milestone.wbsItemUuid),
      descendantMilestonesByWbsItemUuid: new Map(),
    }
  }

  const rowByWbsItemUuid = new Map<string, ProjectPlanNewRow>()
  data.forEach(row => {
    if (row.wbsItemUuid) rowByWbsItemUuid.set(row.wbsItemUuid, row)
  })

  const rootMilestones: MilestoneDetail[] = []
  const descendantMilestonesByWbsItemUuid = new Map<string, MilestoneDetail[]>()
  const rootWbsItemUuid = rootWbsItem?.uuid

  for (const milestone of milestones) {
    if (!milestone.wbsItemUuid) {
      if (!rootWbsItemUuid) {
        rootMilestones.push(milestone)
      }
      continue
    }

    if (rootWbsItemUuid && milestone.wbsItemUuid === rootWbsItemUuid) {
      rootMilestones.push(milestone)
      continue
    }

    // ドリルダウン中のdataはルート自身とその配下のみで構成されるため、
    // 所有行が見つかること自体が「配下である」ことの判定を兼ねる。
    const ownerRow = rowByWbsItemUuid.get(milestone.wbsItemUuid)
    if (!ownerRow) continue

    const list =
      descendantMilestonesByWbsItemUuid.get(ownerRow.wbsItemUuid) ?? []
    list.push(milestone)
    descendantMilestonesByWbsItemUuid.set(ownerRow.wbsItemUuid, list)
  }

  return {
    rootMilestones,
    descendantMilestonesByWbsItemUuid,
  }
}

export const useMilestoneScope = (
  data: ProjectPlanNewRow[],
  milestones: MilestoneDetail[],
  rootWbsItem: NewWbsItemRow | undefined,
  isWbsItemTargetEnabled: boolean
): MilestoneScope =>
  useMemo(
    () =>
      classifyMilestones(data, milestones, rootWbsItem, isWbsItemTargetEnabled),
    [data, milestones, rootWbsItem, isWbsItemTargetEnabled]
  )
